import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@nextui-org/react";
import { FiChevronLeft, FiChevronRight, FiPlus } from "react-icons/fi";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { fr } from "date-fns/locale";

import { isDebriefOverdue, toDebriefDate } from "./debriefUtils";

const WEEK_OPTIONS = { weekStartsOn: 1, locale: fr };
const DAY_KEY_FORMAT = "yyyy-MM-dd";

const CHIP_STYLES = {
  overdue: "bg-red-500/10 text-red-600 hover:bg-red-500/20",
  scheduled: "bg-yellow-500/10 text-yellow-700 hover:bg-yellow-500/20",
  draft: "bg-gray-500/10 text-gray-600 hover:bg-gray-500/20",
  completed: "bg-teal-500/10 text-teal-600 hover:bg-teal-500/20",
};

const DOT_STYLES = {
  overdue: "bg-red-500",
  scheduled: "bg-yellow-500",
  draft: "bg-gray-400",
  completed: "bg-teal-500",
};

const LEGEND = [
  { key: "scheduled", label: "Planifié" },
  { key: "overdue", label: "En retard" },
  { key: "draft", label: "Brouillon" },
  { key: "completed", label: "Terminé" },
];

const getChipKind = (debrief) => {
  if (isDebriefOverdue(debrief)) return "overdue";
  if (debrief.status === "completed") return "completed";
  if (debrief.status === "scheduled") return "scheduled";
  return "draft";
};

const capitalize = (value) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : value);

// Month grid (Monday first) of debriefs placed on their scheduled date.
// Debriefs without a scheduled date are not shown here.
const DebriefsCalendar = ({ debriefs = [], getOperatorName, onOpenDebrief, onCreateForDate }) => {
  const { t } = useTranslation();
  const [month, setMonth] = useState(() => startOfMonth(new Date()));

  const today = startOfDay(new Date());

  const days = useMemo(
    () =>
      eachDayOfInterval({
        start: startOfWeek(startOfMonth(month), WEEK_OPTIONS),
        end: endOfWeek(endOfMonth(month), WEEK_OPTIONS),
      }),
    [month]
  );

  const debriefsByDay = useMemo(() => {
    const grouped = {};
    debriefs.forEach((debrief) => {
      const scheduled = toDebriefDate(debrief.scheduled_date);
      if (!scheduled) return;
      const key = format(scheduled, DAY_KEY_FORMAT);
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(debrief);
    });
    Object.values(grouped).forEach((list) =>
      list.sort((a, b) =>
        getOperatorName(a.operator_id).localeCompare(getOperatorName(b.operator_id))
      )
    );
    return grouped;
  }, [debriefs, getOperatorName]);

  const scheduledInMonth = useMemo(
    () =>
      days
        .filter((day) => isSameMonth(day, month))
        .reduce(
          (total, day) => total + (debriefsByDay[format(day, DAY_KEY_FORMAT)]?.length || 0),
          0
        ),
    [days, month, debriefsByDay]
  );

  const weekdayLabels = days
    .slice(0, 7)
    .map((day) => capitalize(format(day, "EEE", { locale: fr })));

  return (
    <div className="flex flex-col h-full">
      {/* Month navigation */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200">
        <div className="flex items-center gap-2">
          <Button
            isIconOnly
            size="sm"
            variant="light"
            aria-label={t("Mois précédent")}
            onClick={() => setMonth((current) => addMonths(current, -1))}
          >
            <FiChevronLeft size={18} />
          </Button>
          <h2 className="text-lg font-semibold text-gray-900 min-w-[160px] text-center">
            {capitalize(format(month, "MMMM yyyy", { locale: fr }))}
          </h2>
          <Button
            isIconOnly
            size="sm"
            variant="light"
            aria-label={t("Mois suivant")}
            onClick={() => setMonth((current) => addMonths(current, 1))}
          >
            <FiChevronRight size={18} />
          </Button>
          <Button
            size="sm"
            variant="bordered"
            className="ml-2"
            isDisabled={isSameMonth(month, today)}
            onClick={() => setMonth(startOfMonth(new Date()))}
          >
            {t("Aujourd'hui")}
          </Button>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500">
            {scheduledInMonth} {t("debrief(s) planifié(s) ce mois-ci")}
          </span>
          <div className="hidden md:flex items-center gap-3">
            {LEGEND.map((item) => (
              <div
                key={item.key}
                className="flex items-center gap-1.5"
              >
                <span className={`w-2 h-2 rounded-full ${DOT_STYLES[item.key]}`} />
                <span className="text-xs text-gray-500">{t(item.label)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Weekday header */}
      <div className="grid grid-cols-7 border-b border-neutral-200">
        {weekdayLabels.map((label) => (
          <div
            key={label}
            className="px-2 py-2 text-xs font-semibold text-neutral-500 uppercase tracking-wide"
          >
            {label}
          </div>
        ))}
      </div>

      {/* Day grid */}
      <div className="grid grid-cols-7 flex-1 auto-rows-fr">
        {days.map((day, index) => {
          const key = format(day, DAY_KEY_FORMAT);
          const dayDebriefs = debriefsByDay[key] || [];
          const inMonth = isSameMonth(day, month);
          const isToday = isSameDay(day, today);
          // New debriefs can only be planned from today onwards.
          const canCreate = !!onCreateForDate && day >= today;
          const isLastColumn = index % 7 === 6;

          return (
            <div
              key={key}
              // Mouse shortcut on the whole cell; the "+" button below is the
              // keyboard-accessible equivalent.
              onClick={canCreate ? () => onCreateForDate(key) : undefined}
              onKeyDown={() => {}}
              className={`group min-h-[112px] p-1.5 flex flex-col gap-1 border-b border-neutral-200 ${
                isLastColumn ? "" : "border-r"
              } ${inMonth ? "bg-white" : "bg-neutral-50"} ${
                canCreate ? "cursor-pointer hover:bg-indigo-50/40 transition-colors" : ""
              }`}
            >
              <div className="flex items-center justify-between">
                <span
                  className={`w-6 h-6 flex items-center justify-center rounded-full text-xs ${
                    isToday
                      ? "bg-primary text-white font-semibold"
                      : inMonth
                      ? "text-gray-800"
                      : "text-gray-400"
                  }`}
                >
                  {format(day, "d")}
                </span>
                {canCreate && (
                  <button
                    type="button"
                    aria-label={`${t("Planifier un debrief le")} ${format(day, "d MMMM yyyy", {
                      locale: fr,
                    })}`}
                    className="p-0.5 rounded text-primary opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCreateForDate(key);
                    }}
                  >
                    <FiPlus size={14} />
                  </button>
                )}
              </div>
              <div className="flex flex-col gap-1 overflow-y-auto max-h-[96px]">
                {dayDebriefs.map((debrief) => {
                  const kind = getChipKind(debrief);
                  const name = getOperatorName(debrief.operator_id);
                  return (
                    <button
                      key={debrief.id}
                      type="button"
                      title={`${name} · ${t(LEGEND.find((item) => item.key === kind).label)}`}
                      className={`w-full flex items-center gap-1.5 px-1.5 py-1 rounded-md text-left text-xs font-medium transition-colors ${CHIP_STYLES[kind]}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenDebrief(debrief.id);
                      }}
                    >
                      <span className={`w-1.5 h-1.5 shrink-0 rounded-full ${DOT_STYLES[kind]}`} />
                      <span className="truncate">{name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DebriefsCalendar;
